import React from 'react';
import { motion } from 'framer-motion';
import { 
  Eye, 
  ArrowRight, 
  Play, 
  Video, 
  Users, 
  ShoppingBag,
  Sparkles,
  Zap,
  Layers,
  Check,
  ChevronDown
} from 'lucide-react';
import visionLiveLogo from './assets/images/visionlive.png';

// Navbar for Outfit Landing Page
const OutfitNavbar = ({ onTryDemo, onBackToEyewear }) => {
  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 left-0 right-0 z-50 bg-[#0d0d0d] md:bg-[#0d0d0d]/90 md:backdrop-blur-md border-b border-violet-500/10 pt-[env(safe-area-inset-top)]"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <img
            src={visionLiveLogo}
            alt="VisionLive"
            className="h-9 w-auto max-w-[200px] object-contain"
          />
          <div className="flex flex-col">
            <span className="text-xs text-violet-400 -mt-0.5">Outfit</span>
          </div>
        </div>

        {/* Nav Links */}
        <div className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
            Features
          </a>
          <a href="#how-it-works" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
            How it Works
          </a>
          <a href="/#partnership" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
            Partner Launch
          </a>
          <button 
            onClick={onBackToEyewear}
            className="text-sm font-medium text-white/60 hover:text-white transition-colors flex items-center gap-2"
          >
            <span className="px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider bg-blue-500/20 text-blue-400 rounded-full border border-blue-500/30">
              Eyewear
            </span>
          </button>
        </div>

        {/* CTA Buttons */}
        <div className="flex items-center gap-3">
          <span className="px-5 py-2.5 rounded-full bg-white/10 text-sm font-semibold text-white/50 cursor-not-allowed border border-white/10">
            Coming Soon
          </span>
        </div>
      </div>
    </motion.nav>
  );
};

// Hero Section
const OutfitHeroSection = ({ onTryDemo }) => {
  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[#0d0d0d]" />
      <div className="absolute top-0 left-1/4 w-[800px] h-[800px] bg-violet-600/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-[100px]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-radial from-violet-500/5 to-transparent" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            {/* Coming Soon Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/30">
              <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" />
              <span className="text-sm font-bold text-violet-400 uppercase tracking-wider">Coming Soon</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
              AI-Powered
              <br />
              <span className="bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">
                Virtual Try-On
              </span>
              <br />
              for Fashion
            </h1>

            <p className="text-lg text-white/60 max-w-lg leading-relaxed">
              Let your customers see themselves in any outfit with photorealistic rendering 
              powered by <span className="text-violet-400 font-semibold">Google Gemini 2.5</span> and 
              bring looks to life with <span className="text-emerald-400 font-semibold">Veo 4K video generation</span>.
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap gap-3">
              <span className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white/80 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-violet-400" />
                AI Virtual Try-On
              </span>
              <span className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white/80 flex items-center gap-2">
                <Video className="w-4 h-4 text-emerald-400" />
                4K Runway Videos
              </span>
              <span className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white/80 flex items-center gap-2">
                <Layers className="w-4 h-4 text-indigo-400" />
                Compare Looks
              </span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <span className="px-8 py-4 rounded-full bg-white/10 text-base font-semibold text-white/50 cursor-not-allowed border border-white/10 flex items-center justify-center gap-2">
                Coming Soon
              </span>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-4 rounded-full bg-transparent text-base font-semibold text-white border border-white/20 hover:border-violet-500/50 hover:bg-violet-500/5 transition-all flex items-center justify-center gap-2"
              >
                Get Notified
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </div>
          </motion.div>

          {/* Right Content - Visual Preview */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative flex justify-center"
          >
            {/* Main Visual Container */}
            <div className="relative w-full max-w-lg">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/30 to-indigo-500/30 rounded-3xl blur-3xl scale-110" />
              
              {/* Main Card */}
              <div className="relative bg-[#111118] rounded-3xl p-8 border border-white/10 shadow-2xl">
                {/* Preview Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                      <Eye className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-bold text-white">Outfit Try-On</span>
                  </div>
                  <span className="text-xs text-white/40">Powered by Gemini</span>
                </div>

                {/* Mock Preview Grid */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {/* Model Preview */}
                  <div className="aspect-[3/4] rounded-xl bg-gradient-to-br from-violet-900/40 to-indigo-900/40 border border-white/10 flex items-center justify-center">
                    <Users className="w-10 h-10 text-violet-400/60" />
                  </div>
                  {/* Plus Icon */}
                  <div className="aspect-[3/4] rounded-xl flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center border border-white/10">
                      <span className="text-2xl text-white/40">+</span>
                    </div>
                  </div>
                  {/* Garment Preview */}
                  <div className="aspect-[3/4] rounded-xl bg-gradient-to-br from-indigo-900/40 to-violet-900/40 border border-white/10 flex items-center justify-center">
                    <ShoppingBag className="w-10 h-10 text-indigo-400/60" />
                  </div>
                </div>

                {/* Generate Button Mockup */}
                <div className="w-full py-4 rounded-xl bg-gradient-to-r from-violet-600/60 to-indigo-600/60 text-center mb-6">
                  <span className="font-semibold text-white/90">Generate Outfit</span>
                </div>

                {/* Result Preview Area */}
                <div className="aspect-[4/5] rounded-xl bg-gradient-to-br from-gray-900 to-gray-800 border border-white/5 flex flex-col items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(139,92,246,0.03)_50%,transparent_75%)] bg-[length:20px_20px]" />
                  <div className="relative z-10 text-center p-6">
                    <div className="w-20 h-20 rounded-full bg-violet-500/10 flex items-center justify-center mx-auto mb-4 border border-violet-500/20">
                      <Sparkles className="w-10 h-10 text-violet-400" />
                    </div>
                    <p className="text-white/60 text-sm">AI-Generated Result</p>
                    <p className="text-white/30 text-xs mt-1">Photorealistic rendering</p>
                  </div>
                </div>
              </div>

              {/* Floating Badge - Veo Video */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 }}
                className="absolute -right-4 bottom-32 bg-emerald-900/90 border border-emerald-500/30 rounded-2xl p-4 shadow-xl backdrop-blur-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Video className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-emerald-300">4K Veo Videos</p>
                    <p className="text-xs text-emerald-400/60">Runway animations</p>
                  </div>
                </div>
              </motion.div>

              {/* Floating Badge - Compare */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8 }}
                className="absolute -left-4 top-32 bg-indigo-900/90 border border-indigo-500/30 rounded-2xl p-4 shadow-xl backdrop-blur-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                    <Layers className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-indigo-300">Compare Looks</p>
                    <p className="text-xs text-indigo-400/60">Side by side</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span className="text-white/40 text-sm">Scroll to explore</span>
          <ChevronDown className="w-5 h-5 text-white/40 animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
};

// Features Section
const OutfitFeaturesSection = () => {
  const features = [
    {
      icon: Sparkles,
      title: 'AI Virtual Try-On',
      description: 'Our Gemini 2.5 powered AI seamlessly places any garment on your customer, preserving body shape, lighting, and pose with photorealistic quality.',
      color: 'violet'
    },
    {
      icon: Video,
      title: '4K Runway Videos',
      description: 'Generate stunning 4K videos of your customers walking in their selected outfits using Google Veo technology. Perfect for social sharing.',
      color: 'emerald'
    },
    {
      icon: Layers,
      title: 'Compare Looks',
      description: 'Let customers compare two outfits side-by-side in a single view. Help them make confident purchasing decisions.',
      color: 'indigo'
    },
    {
      icon: Zap,
      title: 'Instant Results',
      description: 'Get high-quality try-on images in seconds. Our optimized AI pipeline delivers fast results without compromising quality.',
      color: 'amber'
    }
  ];

  const colorClasses = {
    violet: 'from-violet-500 to-violet-600 text-violet-400 bg-violet-500/10 border-violet-500/20',
    emerald: 'from-emerald-500 to-emerald-600 text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    indigo: 'from-indigo-500 to-indigo-600 text-indigo-400 bg-indigo-500/10 border-indigo-500/20',
    amber: 'from-amber-500 to-amber-600 text-amber-400 bg-amber-500/10 border-amber-500/20'
  };

  return (
    <section id="features" className="py-24 bg-[#0d0d0d] relative">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-violet-400 font-semibold text-sm uppercase tracking-wider">Features</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
            Everything you need for
            <br />
            <span className="bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">
              fashion e-commerce
            </span>
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto text-lg">
            Powered by Google's most advanced AI models, VisionLive Outfit delivers 
            unprecedented virtual try-on experiences.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const colors = colorClasses[feature.color];
            
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#111118] rounded-2xl p-8 border border-white/5 hover:border-white/10 transition-all group"
              >
                <div className={`w-14 h-14 rounded-xl ${colors.split(' ')[2]} ${colors.split(' ')[3]} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <Icon className={`w-7 h-7 ${colors.split(' ')[1]}`} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-white/60 leading-relaxed">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      step: '01',
      title: 'Upload a Photo',
      description: 'Customer uploads a full-body photo or uses one of our model templates.'
    },
    {
      step: '02',
      title: 'Select Garment',
      description: 'Browse your catalog and select any garment to try on virtually.'
    },
    {
      step: '03',
      title: 'AI Generation',
      description: 'Our Gemini AI generates a photorealistic image of the customer wearing the garment.'
    },
    {
      step: '04',
      title: 'Share & Convert',
      description: 'Generate runway videos and share to social media, driving conversions.'
    }
  ];

  return (
    <section id="how-it-works" className="py-24 bg-[#0a0a0f] relative">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-violet-400 font-semibold text-sm uppercase tracking-wider">How It Works</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
            Simple. Fast. Powerful.
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto text-lg">
            Get started in minutes with our intuitive virtual try-on solution.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-4 gap-8">
          {steps.map((item, index) => (
            <motion.div
              key={item.step}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-violet-500/20">
                <span className="text-2xl font-bold text-white">{item.step}</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3">{item.title}</h3>
              <p className="text-white/60 text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Partner Access Section
const OutfitPartnershipSection = () => {
  return (
    <section id="partner-access" className="py-24 bg-[#0d0d0d] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(139,92,246,0.14),transparent_36%)]" />
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-violet-400 font-semibold text-sm uppercase tracking-wider">Partner Access</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
            Private rollout instead of public pricing
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto text-lg">
            VisionLive Outfit is being positioned through curated launches and partner-led onboarding rather than a public price table.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-xl mx-auto"
        >
          <div className="bg-gradient-to-br from-violet-900/30 to-indigo-900/30 rounded-3xl p-8 md:p-12 border border-violet-500/20 text-center">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-violet-500/30">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">Request private access</h3>
            <p className="text-white/60 mb-8">
              Tell us about your catalog and launch goals. New access is opened selectively while the partnership model stays in focus.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <motion.a
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                href="mailto:sales@vltryon.com?subject=VisionLive%20Outfit%20Private%20Access"
                className="px-8 py-4 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-base font-semibold text-white hover:from-violet-500 hover:to-indigo-500 transition-all shadow-lg shadow-violet-500/20"
              >
                Request Access
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                href="/#partnership"
                className="px-8 py-4 rounded-xl border border-white/15 bg-white/5 text-base font-semibold text-white hover:border-violet-500/40 hover:bg-violet-500/10 transition-all"
              >
                See eyewear partnership
              </motion.a>
            </div>
            <div className="flex items-center justify-center gap-6 mt-8 text-sm text-white/40">
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-400" />
                No public signup
              </span>
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-400" />
                Partner-reviewed onboarding
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// CTA Section
const OutfitCTASection = ({ onTryDemo }) => {
  return (
    <section className="py-24 bg-[#0a0a0f] relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-violet-900/20 via-transparent to-indigo-900/20" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to transform your
            <br />
            <span className="bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">
              fashion e-commerce?
            </span>
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto text-lg mb-8">
            Experience the future of virtual try-on technology. Coming soon.
          </p>
          <span className="px-10 py-5 rounded-full bg-white/10 text-lg font-semibold text-white/50 cursor-not-allowed border border-white/10 flex items-center gap-3 mx-auto w-fit">
            Coming Soon
          </span>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const OutfitFooter = ({ onNavigateToTerms, onNavigateToPrivacy, onNavigateToCookiePolicy, onNavigateToApiDocs }) => {
  const handleTermsClick = (e) => {
    if (onNavigateToTerms) {
      e.preventDefault();
      onNavigateToTerms();
    }
  };
  const handlePrivacyClick = (e) => {
    if (onNavigateToPrivacy) {
      e.preventDefault();
      onNavigateToPrivacy();
    }
  };
  const handleCookiePolicyClick = (e) => {
    if (onNavigateToCookiePolicy) {
      e.preventDefault();
      onNavigateToCookiePolicy();
    }
  };
  const handleApiDocsClick = (e) => {
    if (onNavigateToApiDocs) {
      e.preventDefault();
      onNavigateToApiDocs();
    }
  };

  return (
    <footer className="py-12 bg-[#0d0d0d] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img
              src={visionLiveLogo}
              alt="VisionLive"
              className="h-8 w-auto max-w-[200px] object-contain"
            />
            <div className="flex flex-col">
              <span className="text-xs text-violet-400 -mt-0.5">Outfit</span>
            </div>
          </div>
          <p className="text-white/40 text-sm">
            © 2026 VisionLive. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-white/40 text-sm">
            <a
              href="/api-docs"
              onClick={handleApiDocsClick}
              className="hover:text-white transition-colors"
            >
              API Docs
            </a>
            <a
              href="/privacy"
              onClick={handlePrivacyClick}
              className="hover:text-white transition-colors"
            >
              Privacy
            </a>
            <a
              href="/terms"
              onClick={handleTermsClick}
              className="hover:text-white transition-colors"
            >
              Terms
            </a>
            <a
              href="/privacy#cookies"
              onClick={handleCookiePolicyClick}
              className="hover:text-white transition-colors"
            >
              Cookie Policy
            </a>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('cookie-preferences-requested'))}
              className="hover:text-white transition-colors"
            >
              Cookie preferences
            </button>
            <button className="hover:text-white transition-colors">Contact</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
const OutfitLandingPage = ({ onNavigateToDemo, onNavigateToEyewear, onNavigateToTerms, onNavigateToPrivacy, onNavigateToCookiePolicy, onNavigateToApiDocs }) => {
  const handleTryDemo = () => {
    if (onNavigateToDemo) {
      onNavigateToDemo();
    }
  };

  const handleBackToEyewear = () => {
    if (onNavigateToEyewear) {
      onNavigateToEyewear();
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white font-sans antialiased">
      <OutfitNavbar onTryDemo={handleTryDemo} onBackToEyewear={handleBackToEyewear} />
      <OutfitHeroSection onTryDemo={handleTryDemo} />
      <OutfitFeaturesSection />
      <HowItWorksSection />
      <OutfitPartnershipSection />
      <OutfitCTASection onTryDemo={handleTryDemo} />
      <OutfitFooter
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookiePolicy={onNavigateToCookiePolicy}
        onNavigateToApiDocs={onNavigateToApiDocs}
      />
    </div>
  );
};

export default OutfitLandingPage;
